import {
  type ConfirmFacePhotoUploadResult,
  FACE_ASSET_CONTENT_TYPES,
  type FaceAssetPresignUploadInput,
  type FaceAssetUploadPurpose,
  type FacePhotoSize,
  MAX_IMAGE_UPLOAD_BYTES,
} from '@repo/api-contracts';
import { useEffect, useRef, useState } from 'react';
import type { StoreApi } from 'zustand/vanilla';
import type { EditAreaDraftState } from '@/entities/edit-area';
import { errorCodeOf } from '@/shared/api';
import { readPhotoSize } from './read-photo-size';
import { useFaceAssetMutations } from './use-face-asset-mutations';

/** 업로드 훅이 보는 초안 상태 — 사진 전용 store 와 전체 편집 store 가 함께 갖는 부분이다. */
type UploadDraftState = Pick<EditAreaDraftState, 'generation' | 'operations' | 'draft'>;
type OperationStatus = EditAreaDraftState['operations'][string]['status'];

export interface FaceAssetUploadState {
  readonly phase: 'idle' | 'uploading' | 'reading-size' | 'confirming' | 'ready' | 'error';
  readonly error: string | null;
  readonly canRetry: boolean;
}
export interface FaceAssetUpload {
  readonly state: FaceAssetUploadState;
  readonly upload: (file: File) => Promise<void>;
  readonly retry: () => Promise<void>;
  readonly cancel: () => void;
}
/** 확정된 면 에셋 — 저장 키·px 크기·표시 URL. */
export interface ConfirmedFaceAsset {
  readonly key: string;
  readonly size: FacePhotoSize;
  readonly url: string;
}

/**
 * 면 사진·합성 에셋이 공유하는 업로드 흐름의 바깥 — 작업 칸, 용도, 형식, 확정 결과 검사, 초안 반영.
 * 사진 훅과 합성 훅이 이 포트만 다르게 채운다.
 */
export interface FaceAssetUploadPort<TState extends UploadDraftState> {
  readonly store: Pick<StoreApi<TState>, 'getState' | 'subscribe'>;
  /** operations 표의 칸 키. 같은 칸에는 전송 하나만 돈다. */
  readonly slot: string;
  /** presign 용도. 브라우저가 먼저 거르는 형식은 계약 표 FACE_ASSET_CONTENT_TYPES[purpose] 다. */
  readonly purpose: FaceAssetUploadPurpose;
  /** 허용하지 않는 형식을 골랐을 때의 거부 문구. */
  readonly contentTypeMessage: string;
  /** 안내·오류 문구에 들어가는 에셋 이름('사진'·'마스크'·'음영'·'안내선'). */
  readonly label: string;
  /** 이 칸이 아직 살아 있는가(면 사용 중·색상 존재 등). 아니면 진행 중 업로드를 무효로 한다. */
  readonly isSlotAlive: (state: TState) => boolean;
  readonly begin: (operationId: string) => boolean;
  readonly setStatus: (operationId: string, status: OperationStatus) => void;
  readonly cancel: (operationId: string) => void;
  /** 확정 결과를 저장 키·크기·URL 로 바꾼다. 이 종류·이 상품의 키가 아니면 null. */
  readonly parseConfirmed: (
    result: ConfirmFacePhotoUploadResult,
    productId: string,
  ) => ConfirmedFaceAsset | null;
  /** 초안에 반영한다. 잠금·낡은 작업이면 false 를 돌려주고 초안을 바꾸지 않는다. */
  readonly reflect: (operationId: string, asset: ConfirmedFaceAsset) => boolean;
  /** 확정 거부 중 파일 자체의 문제(알파 없음 등)라 같은 파일로 다시 시도할 수 없는 tRPC 오류 코드. */
  readonly fileRejectedCodes?: readonly string[];
}

interface UploadSession {
  readonly uploadId: string;
  readonly expiresAt: number;
  readonly photoSize: FacePhotoSize;
}
interface UploadOperation {
  readonly id: string;
  readonly generation: number;
  readonly controller: AbortController;
}

const INITIAL_STATE: FaceAssetUploadState = { phase: 'idle', error: null, canRetry: false };
/** 확정 세션을 버려야 하는 응답 코드 — 다음 시도는 새 presign 부터 한다. */
const TERMINAL_CONFIRM_CODES = [
  'BAD_REQUEST',
  'NOT_FOUND',
  'FORBIDDEN',
  'UNAUTHORIZED',
  'CONFLICT',
];
const PHASE_LABELS = { uploading: '업로드', 'reading-size': '이미지 읽기' } as const;

/**
 * 칸당 전송 하나. presign → 임시 PUT → 크기 읽기 → confirm → 초안 반영. 성공 전까지 기존 값을 유지하고,
 * 취소·세대 변경·칸 교체·칸 소멸 뒤에 도착한 응답은 상태에 전달하지 않는다.
 */
export const useFaceAssetUpload = <TState extends UploadDraftState>(
  input: FaceAssetUploadPort<TState>,
): FaceAssetUpload => {
  const mutations = useFaceAssetMutations();
  const [state, setState] = useState<FaceAssetUploadState>(INITIAL_STATE);
  const port = useRef(input);
  port.current = input;
  const active = useRef<UploadOperation | null>(null);
  const selectedFile = useRef<File | null>(null);
  const session = useRef<UploadSession | null>(null);
  const running = useRef(false);
  const { store, slot } = input;

  const cancel = (): void => {
    const operation = active.current;
    active.current = null;
    running.current = false;
    operation?.controller.abort();
    if (operation) port.current.cancel(operation.id);
    selectedFile.current = null;
    session.current = null;
    setState(INITIAL_STATE);
  };

  useEffect(() => {
    const unsubscribe = store.subscribe((next, previous) => {
      const operation = active.current;
      const invalidOperation = operation !== null && next.operations[slot]?.id !== operation.id;
      if (
        next.generation === previous.generation &&
        !invalidOperation &&
        port.current.isSlotAlive(next)
      )
        return;
      active.current = null;
      running.current = false;
      operation?.controller.abort();
      selectedFile.current = null;
      session.current = null;
      setState(INITIAL_STATE);
    });
    return () => {
      unsubscribe();
      const operation = active.current;
      active.current = null;
      operation?.controller.abort();
      if (operation) port.current.cancel(operation.id);
    };
  }, [store, slot]);

  const run = async (file: File, reuseSession: boolean): Promise<void> => {
    if (running.current) return;
    const { label, purpose, contentTypeMessage } = port.current;
    const operation: UploadOperation = {
      id: crypto.randomUUID(),
      generation: store.getState().generation,
      controller: new AbortController(),
    };
    // Store subscribers run synchronously. Register the replacement before begin
    // so an intentional retry cannot be mistaken for an external cancellation.
    const previousOperation = active.current;
    active.current = operation;
    if (!port.current.begin(operation.id)) {
      active.current = previousOperation;
      return;
    }
    running.current = true;
    selectedFile.current = file;
    if (!reuseSession) session.current = null;
    const valid = (): boolean =>
      active.current === operation &&
      store.getState().generation === operation.generation &&
      !operation.controller.signal.aborted &&
      store.getState().operations[slot]?.id === operation.id;
    let phase: 'uploading' | 'reading-size' | 'confirming' = 'uploading';
    let canRetry = false;
    const updatePhase = (next: typeof phase): void => {
      phase = next;
      if (!valid()) return;
      setState({ phase, error: null, canRetry: false });
      port.current.setStatus(operation.id, phase);
    };
    try {
      updatePhase('uploading');
      if (!(FACE_ASSET_CONTENT_TYPES[purpose] as readonly string[]).includes(file.type))
        throw new Error(contentTypeMessage);
      if (file.size === 0 || file.size > MAX_IMAGE_UPLOAD_BYTES)
        throw new Error('이미지는 0바이트 초과, 10MiB 이하여야 합니다.');
      canRetry = true;
      const productId = store.getState().draft.productId;
      let confirmedSession = session.current;
      if (!confirmedSession || confirmedSession.expiresAt <= Date.now()) {
        const requestedAt = Date.now();
        // 형식은 바로 위에서 같은 계약 표로 걸렀다 — 용도별 입력 union 으로의 단언은 그 검사에 기댄다.
        const signed = await mutations.presign({
          productId,
          purpose,
          contentType: file.type,
        } as FaceAssetPresignUploadInput);
        if (!valid()) return;
        if (!('purpose' in signed) || signed.purpose !== purpose)
          throw new Error(`${label} 업로드를 준비하지 못했습니다.`);
        const response = await fetch(signed.uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'content-type': file.type },
          signal: operation.controller.signal,
        });
        if (!valid()) return;
        if (!response.ok) throw new Error(`파일 업로드에 실패했습니다 (HTTP ${response.status}).`);
        phase = 'reading-size';
        updatePhase(phase);
        canRetry = false;
        const photoSize = await readPhotoSize(file, operation.controller.signal);
        if (!valid()) return;
        confirmedSession = {
          uploadId: signed.uploadId,
          expiresAt: requestedAt + signed.confirmExpiresInSeconds * 1000,
          photoSize,
        };
        session.current = confirmedSession;
      }
      phase = 'confirming';
      updatePhase(phase);
      canRetry = true;
      const result = await mutations.confirm({
        productId,
        uploadId: confirmedSession.uploadId,
        photoSize: confirmedSession.photoSize,
      });
      if (!valid()) return;
      const asset = port.current.parseConfirmed(result, productId);
      if (asset === null) {
        canRetry = false;
        session.current = null;
        throw new Error(`확정된 ${label} 정보를 확인할 수 없습니다. 파일을 다시 선택해 주세요.`);
      }
      if (
        asset.size.widthPx !== confirmedSession.photoSize.widthPx ||
        asset.size.heightPx !== confirmedSession.photoSize.heightPx
      ) {
        canRetry = false;
        session.current = null;
        throw new Error('브라우저와 서버의 이미지 크기가 다릅니다. 파일을 다시 선택해 주세요.');
      }
      if (port.current.reflect(operation.id, asset)) {
        if (store.getState().generation !== operation.generation) return;
        selectedFile.current = null;
        session.current = null;
        active.current = null;
        setState({ phase: 'ready', error: null, canRetry: false });
      } else if (valid()) {
        // 저장 버전 요청이 반영을 잠갔더라도 영구 confirming 상태를 남기지 않는다.
        // 실패한 버전 요청 후에는 확정된 같은 세션을 다시 반영할 수 있다.
        throw new Error(`현재 ${label} 편집이 잠겨 있습니다. 요청 완료 후 다시 시도해 주세요.`);
      }
    } catch (cause) {
      if (!valid()) return;
      const code = phase === 'confirming' ? errorCodeOf(cause) : null;
      if (code !== null && TERMINAL_CONFIRM_CODES.includes(code)) session.current = null;
      // 알파 없음 같은 파일 자체의 거부는 같은 파일을 다시 올려도 같은 결과다 — 재시도 대신 다른 파일을 안내한다.
      const fileRejected = code !== null && (port.current.fileRejectedCodes ?? []).includes(code);
      if (fileRejected) canRetry = false;
      const message = cause instanceof Error ? cause.message : '알 수 없는 오류가 발생했습니다.';
      const phaseLabel = phase === 'confirming' ? `${label} 검증` : PHASE_LABELS[phase];
      setState({
        phase: 'error',
        error: `${phaseLabel} 실패: ${message}${fileRejected ? ' 다른 파일을 선택해 주세요.' : ''}`,
        canRetry,
      });
      port.current.setStatus(operation.id, 'error');
    } finally {
      if (active.current === operation || active.current === null) running.current = false;
    }
  };

  return {
    state,
    upload: (file) => run(file, false),
    retry: async () => {
      if (selectedFile.current && state.canRetry) await run(selectedFile.current, true);
    },
    cancel,
  };
};
